import { useState } from "react";
import { useCentralStore } from "../../store/Central";
import { ShareConnectRelationshipWithClient } from "./existing-relationship/share-connect-relationship-with-client-dialog.component";
import { NewConnectRelationship } from "./new-relationship/new-connect-relationship-dialog.component";
import { ShareConnectRelationshipWithVendor } from "./existing-relationship/share-connect-relationship-with-vendor-dialog.component";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { ChevronsUpDown } from "lucide-react";

/**
 * SectionForNotConnected - Manages three different ways to establish dentCONNECT relationships (=== client.connected_organization_id is not null):
 * 
 * 1. **NewConnectRelationship** (Primary method - always visible):
 *    - Creates a brand new connection with a practice or lab
 *    - Uses external API to create/retrieve user organization
 *    - Calls RPC: CONNECT_CLIENT with the current org as vendor
 *    - Suitable for: Connecting with new customers who aren't in the system yet
 * 
 * 2. **ShareConnectRelationshipWithClient** (Collapsible option):
 *    - Shares an existing connection with another client within the same organization
 *    - No RPC call - only updates the connected_organization_id in the clients table
 *    - This makes sense because the vendor-customer relationship was already established
 * 
 * 3. **ShareConnectRelationshipWithVendor** (Collapsible option):
 *    - Reverses an existing vendor relationship to also become their customer
 *    - Calls RPC: CONNECT_CLIENT with role reversal (vendor becomes customer)
 */
export const SectionForNotConnected = () => {
	const [isConnectToExistingClient, setIsConnectToExistingClient] = useState(false);
	const [isConnectToExistingVendor, setIsConnectToExistingVendor] = useState(false);
	return (
		<div className="my-8 max-w-xl mx-auto">
			<div className="flex flex-col items-center mb-8">
				<img
					src="/images/dentconnect.png"
					alt="dentCONNECT"
					className="h-20 w-auto mb-4"
				/>
				<h2 className="text-2xl font-medium text-gray-800">Mit dentCONNECT verbinden</h2>
				<p className="text-sm text-gray-500 mt-2 text-center max-w-md">
					Verbinden Sie sich mit Ihren Kunden für nahtlose Kommunikation und Zusammenarbeit. Die Verbindung ist für Sie und Ihre Kunden kostenlos.
				</p>
			</div>
			<div>

				<div className="p-6">
					<NewConnectRelationship />

					<Collapsible open={isConnectToExistingClient} onOpenChange={setIsConnectToExistingClient}
						className="mt-6"
					>
						<CollapsibleTrigger>
							<div className="flex items-center gap-2">
								<ChevronsUpDown className="h-4 w-4" />
								<h3 className="text-sm font-medium text-gray-700">Bestehenden Kunden verbinden</h3>
							</div>
						</CollapsibleTrigger>
						<CollapsibleContent>
							<ShareConnectRelationshipWithClient />
						</CollapsibleContent>
					</Collapsible>
					<Collapsible
						open={isConnectToExistingVendor}
						onOpenChange={setIsConnectToExistingVendor}
						className="border-t-0"
					>
						<CollapsibleTrigger>
							<div className="flex items-center gap-2">
								<ChevronsUpDown className="h-4 w-4" />
								<h3 className="text-sm font-medium text-gray-700">Bestehende Fremdlabore auch als Kunden nutzen</h3>
							</div>
						</CollapsibleTrigger>
						<CollapsibleContent>
							<ShareConnectRelationshipWithVendor />
						</CollapsibleContent>
					</Collapsible>
				</div>
			</div>
		</div>
	);
};
